import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import { Link, useSearchParams } from 'react-router-dom';
import { getApiErrorMessage, handleApiCall } from '../../apis';
import { endpoint } from '../../utils/endpoints';
import PermissionGate from '../../Components/PermissionGate/PermissionGate';
import { MODULES } from '../../utils/permissions';
import EditIcon from '../../assets/image/edit-icon.svg'
import EyeIcon from '../../assets/image/eye-icon.svg'
import LeftAngle from '../../assets/image/left-angle.svg'
import RightAngle from '../../assets/image/right-angle.svg'
import FilterIcon from '../../assets/image/filter.svg'
import MasterSuccessModal from '../../Pages/MasterManagement/MasterSuccessModal';
import DateRangeFilter from '../../Components/DateRangeFilter/DateRangeFilter';
import DateRangeAppliedLabel from '../../Components/DateRangeFilter/DateRangeAppliedLabel';
import { appendDateRangeParams, DATE_PARAM_KEYS } from '../../utils/dateRange';
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import { useListPersistence } from "../../hooks/useListPersistence";
import { readListLimit, readListPage } from "../../hooks/useListQueryPersistence";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const LIST_PATH = "/driver-management";

const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.data)) return value.data;
    return [];
};

const mapLocation = (item) => ({
    id: item?._id || item?.id || '',
    name: item?.name || item?.location_name || '—',
});

const filterByQuery = (items, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.name.toLowerCase().includes(q));
};

const FilterChevron = ({ open }) => (
    <span
        className={`inline-block text-gray-400 transition-transform duration-200 ${open ? '-rotate-90' : ''}`}
        aria-hidden
    >
        ›
    </span>
);

const mapDriverRow = (r) => ({
    id: r?._id || r?.id || '',
    driverId: r?.driver_ref_id != null ? String(r.driver_ref_id) : '—',
    name: r?.full_name ?? r?.name ?? '—',
    mobile: (() => {
        const code = (r?.country_code || '').trim();
        const num = (r?.mobile_number ?? r?.mobile ?? '').toString().trim();
        if (code && num) return `${code} ${num}`;
        return num || code || '—';
    })(),
    licence: r?.license_number ?? r?.licence_number ?? '—',
    accountant: r?.accountant_name ?? r?.accountant?.full_name ?? '—',
    location:
        r?.mipl_location_id?.name ||
        r?.mipl_location?.name ||
        r?.location_name ||
        '—',
    statusCode: r?.status === 1 ? 1 : 0,
    status: r?.status === 1 ? 'Active' : 'Inactive',
});

const extractDriverList = (d) => {
    const raw =
        d?.driver_data ||
        d?.drivers ||
        (Array.isArray(d) ? d : null);
    return Array.isArray(raw) ? raw : [];
};

const DriverManagement = () => {
    const filterRef = useRef(null);
    const [searchParams] = useSearchParams();

    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState(() => searchParams.get("search") || '');
    const [debouncedSearch, setDebouncedSearch] = useState(() =>
        clampSearchInput(searchParams.get("search") || '').trim()
    );
    const [currentPage, setCurrentPage] = useState(() => readListPage(searchParams));
    const [itemsPerPage, setItemsPerPage] = useState(() => readListLimit(searchParams));
    const [statusUpdatingId, setStatusUpdatingId] = useState('');
    const [successModal, setSuccessModal] = useState({ open: false, message: '' });

    const [filterOpen, setFilterOpen] = useState(false);
    const [filterSub, setFilterSub] = useState(null);
    const [miplLocationId, setMiplLocationId] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [appliedMiplLocationId, setAppliedMiplLocationId] = useState(
        () => searchParams.get("mipl_location_id") || ''
    );
    const [appliedDateFrom, setAppliedDateFrom] = useState(
        () => searchParams.get("from_date") || ''
    );
    const [appliedDateTo, setAppliedDateTo] = useState(
        () => searchParams.get("to_date") || ''
    );

    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');

    const closeMasterSuccess = () => setSuccessModal({ open: false, message: '' });

    const applyRestoredState = useCallback((saved) => {
        if (saved.search != null) {
            setSearch(saved.search);
            setDebouncedSearch(clampSearchInput(saved.search).trim());
        }
        if (saved.page) {
            const p = Number(saved.page);
            if (Number.isFinite(p) && p > 0) setCurrentPage(p);
        }
        if (saved.limit) {
            const l = Number(saved.limit);
            if (Number.isFinite(l) && l > 0) setItemsPerPage(l);
        }
        if (saved.mipl_location_id) {
            setAppliedMiplLocationId(saved.mipl_location_id);
            setMiplLocationId(saved.mipl_location_id);
        }
        if (saved.from_date) {
            setAppliedDateFrom(saved.from_date);
            setDateFrom(saved.from_date);
        }
        if (saved.to_date) {
            setAppliedDateTo(saved.to_date);
            setDateTo(saved.to_date);
        }
    }, []);

    const { skipPageResetRef, detailListQuery } = useListPersistence(
        LIST_PATH,
        {
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearch,
            mipl_location_id: appliedMiplLocationId || undefined,
            from_date: appliedDateFrom || undefined,
            to_date: appliedDateTo || undefined,
        },
        applyRestoredState
    );

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    useLayoutEffect(() => {
        if (skipPageResetRef.current) return;
        setCurrentPage(1);
    }, [debouncedSearch, appliedMiplLocationId, appliedDateFrom, appliedDateTo, skipPageResetRef]);

    useEffect(() => {
        const onDoc = (e) => {
            if (!filterOpen) return;
            const el = filterRef.current;
            if (el && !el.contains(e.target)) {
                setFilterOpen(false);
                setFilterSub(null);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [filterOpen]);

    useEffect(() => {
        if (!filterOpen || filterSub !== 'location') return;
        let cancelled = false;
        const loadLocations = async () => {
            setLocationsLoading(true);
            try {
                const res = await handleApiCall('get', endpoint.listOfAlllocation, {});
                if (cancelled) return;
                if (res?.data?.code === 200) {
                    const d = res?.data?.data;
                    const raw = Array.isArray(d) ? d : toArray(d?.location_data ?? d?.locations);
                    setLocations(raw.map(mapLocation).filter((x) => x.id));
                } else {
                    notifyError(getApiErrorMessage(res, 'Failed to load locations'));
                    setLocations([]);
                }
            } catch {
                if (!cancelled) {
                    notifyError('Failed to load locations');
                    setLocations([]);
                }
            } finally {
                if (!cancelled) setLocationsLoading(false);
            }
        };
        loadLocations();
        return () => {
            cancelled = true;
        };
    }, [filterOpen, filterSub]);

    const searchForApi = (() => {
        const s = clampSearchInput(debouncedSearch).trim();
        if (!s) return '';
        if (/^\+?[\d\s-]+$/.test(s)) return s.replace(/\D/g, '');
        return s;
    })();

    const fetchDriverList = async () => {
        setListLoading(true);
        try {
            const params = new URLSearchParams({
                search: searchForApi,
                page: String(currentPage),
                limit: String(itemsPerPage),
            });
            if (appliedMiplLocationId) params.set('mipl_location_id', appliedMiplLocationId);
            appendDateRangeParams(
                params,
                { start: appliedDateFrom, end: appliedDateTo },
                DATE_PARAM_KEYS.fromTo
            );

            const response = await handleApiCall(
                'get',
                `${endpoint.listOfDriver}?${params.toString()}`,
                {}
            );
            if (response?.data?.code === 200) {
                const d = response?.data?.data || {};
                const list = extractDriverList(d);
                const mapped = list.map(mapDriverRow);
                setRows(mapped);
                setTotalRecords(d?.total ?? mapped.length);
                setTotalPages(Math.max(1, d?.total_pages ?? 1));
            } else {
                notifyError(getApiErrorMessage(response, 'Failed to load drivers'));
                setRows([]);
                setTotalRecords(0);
                setTotalPages(1);
            }
        } catch {
            notifyError('Failed to load drivers');
            setRows([]);
            setTotalRecords(0);
            setTotalPages(1);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        fetchDriverList();
    }, [
        debouncedSearch,
        currentPage,
        itemsPerPage,
        appliedMiplLocationId,
        appliedDateFrom,
        appliedDateTo,
    ]);

    const hasActiveFilters = Boolean(
        appliedMiplLocationId || appliedDateFrom || appliedDateTo
    );
    const hasPartialDateRange = Boolean((dateFrom && !dateTo) || (!dateFrom && dateTo));
    const hasCompleteDateRange = Boolean(dateFrom && dateTo);
    const hasInvalidDateRange = hasCompleteDateRange && dateFrom > dateTo;
    const hasFilterValues = Boolean(
        hasActiveFilters || miplLocationId || dateFrom || dateTo
    );
    const canApplyFilters =
        Boolean(miplLocationId || hasCompleteDateRange) &&
        !hasPartialDateRange &&
        !hasInvalidDateRange;

    const applyFilters = () => {
        setAppliedMiplLocationId(miplLocationId.trim());
        setAppliedDateFrom(dateFrom);
        setAppliedDateTo(dateTo);
        setFilterOpen(false);
        setFilterSub(null);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setMiplLocationId('');
        setDateFrom('');
        setDateTo('');
        setAppliedMiplLocationId('');
        setAppliedDateFrom('');
        setAppliedDateTo('');
        setLocationSearch('');
        setFilterOpen(false);
        setFilterSub(null);
        setCurrentPage(1);
    };

    const handleLocationSelect = (id) => {
        setMiplLocationId(id);
        setFilterSub(null);
    };

    const handleDriverStatusToggle = async (driverId, currentStatus) => {
        const nextStatus = currentStatus === 1 ? 0 : 1;
        if (!driverId) return;
        setStatusUpdatingId(driverId);
        try {
            const response = await handleApiCall('put', endpoint.updateStatusDriver, {
                driver_id: driverId,
                status: nextStatus,
            });
            if (response?.data?.code === 200) {
                setRows((prev) =>
                    prev.map((r) =>
                        r.id === driverId
                            ? {
                                  ...r,
                                  statusCode: nextStatus,
                                  status: nextStatus === 1 ? 'Active' : 'Inactive',
                              }
                            : r
                    )
                );
                notifySuccess(response?.data?.message || 'Driver status updated');
                return;
            }
            notifyError(getApiErrorMessage(response, 'Could not update driver status'));
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setStatusUpdatingId('');
        }
    };

    const filteredLocations = filterByQuery(locations, locationSearch);
    const displayFrom = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const displayTo = Math.min(currentPage * itemsPerPage, totalRecords);

    return (
        <>
        <Toastify/>
            <div className="tab-pane-content mt-2.5 md:mt-7">

                <div className="md:mb-4 mb-2.5">
                    <div className='flex flex-wrap items-center justify-end gap-2.5'>
                        <input
                            type="text"
                            placeholder="Search..."
                            className="searchIcon min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:!shadow-sm sm:max-w-[min(100%,28rem)]"
                            value={search}
                            maxLength={SEARCH_MAX_LENGTH}

                            onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                        />
                        <div className='max-[767px]:hidden'>
                            <DateRangeAppliedLabel
                                from={appliedDateFrom}
                                to={appliedDateTo}
                                prefix="Date range: "
                            />
                            {hasActiveFilters && (
                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-UbuntuMedium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                        <div className="relative flex-shrink-0" ref={filterRef}>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilterOpen((o) => !o);
                                    setFilterSub(null);
                                }}
                                className="inline-flex md:h-[42px] md:w-[42px] h-[36px] w-[36px] items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
                                aria-label="Filters"
                            >
                                <img
                                    src={FilterIcon}
                                    alt=""
                                    className="md:h-5 md:w-5 h-4 w-4 invert grayscale brightness-100"
                                />
                            </button>
                            {filterOpen && (
                                <div className="absolute right-0 z-50 mt-2 min-w-[240px] rounded-lg border border-primary/25 bg-white py-2 shadow-lg">
                                    <button
                                        type="button"
                                        className="flex w-full items-center justify-between px-4 py-2 text-left text-sm text-[#030253] hover:bg-primary/5"
                                        onClick={() =>
                                            setFilterSub(filterSub === 'location' ? null : 'location')
                                        }
                                    >
                                        MIPL Location
                                        <FilterChevron open={filterSub === 'location'} />
                                    </button>
                                    {filterSub === 'location' && (
                                        <div className="border-t border-gray-100 px-3 py-2">
                                            <input

                                                type="search"
                                                placeholder="Search"
                                                value={locationSearch}
                                                maxLength={SEARCH_MAX_LENGTH}

                                                onChange={(e) => setLocationSearch(clampSearchInput(e.target.value))}
                                                className="searchIcon mb-2 w-full rounded border border-primary/30 px-2 py-1.5 text-sm outline-none focus:border-primary"
                                            />
                                            <div className="max-h-40 overflow-y-auto">
                                                {locationsLoading ? (
                                                    <p className="mb-0 px-1 py-2 text-xs text-gray-500">
                                                        Loading…
                                                    </p>
                                                ) : filteredLocations.length === 0 ? (
                                                    <p className="mb-0 px-1 py-2 text-xs text-gray-500">
                                                        No locations found
                                                    </p>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={`block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-primary/5 ${
                                                                !miplLocationId
                                                                    ? 'font-UbuntuMedium text-primary'
                                                                    : 'text-gray-800'
                                                            }`}
                                                            onClick={() => handleLocationSelect('')}
                                                        >
                                                            All locations
                                                        </button>
                                                        {filteredLocations.map((loc) => (
                                                            <button
                                                                key={loc.id}
                                                                type="button"
                                                                className={`block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-primary/5 ${
                                                                    miplLocationId === loc.id
                                                                        ? 'font-UbuntuMedium text-primary'
                                                                        : 'text-gray-800'
                                                                }`}
                                                                onClick={() => handleLocationSelect(loc.id)}
                                                            >
                                                                {loc.name}
                                                            </button>
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="flex w-full items-center justify-between border-t border-gray-100 px-4 py-2 text-left text-sm text-[#030253] hover:bg-primary/5"
                                        onClick={() => setFilterSub(filterSub === 'dates' ? null : 'dates')}
                                    >
                                        Date Range
                                        <FilterChevron open={filterSub === 'dates'} />
                                    </button>
                                    {filterSub === 'dates' && (
                                        <div className="border-t border-gray-100 px-4 py-3">
                                            <DateRangeFilter
                                                variant="embedded"
                                                from={dateFrom}
                                                to={dateTo}
                                                onChange={({ from, to }) => {
                                                    setDateFrom(from);
                                                    setDateTo(to);
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="flex gap-2 border-t border-gray-100 px-4 py-2">
                                        {hasFilterValues && (
                                            <button
                                                type="button"
                                                className="btn w-full !rounded-lg !border-gray-300 !bg-white !py-2 text-sm !text-gray-800 hover:!bg-gray-50"
                                                onClick={clearFilters}
                                            >
                                                Clear
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-primary w-full !rounded-lg !py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                                            disabled={!canApplyFilters}
                                            onClick={applyFilters}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='text-center mt-2 [&_.h-11]:h-auto flex items-cener justify-between [&_.inline-flex]:text-xs md:hidden'>
                        <DateRangeAppliedLabel
                            from={appliedDateFrom}
                            to={appliedDateTo}
                            prefix="Date range: "
                        />
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="rounded-lg border border-gray-300 bg-white ml-1 !px-3.5 py-2 text-xs font-UbuntuMedium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50">
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Driver ID</th>
                                    <th>Driver Name</th>
                                    <th>Mobile Number</th>
                                    <th>Licence Number</th>
                                    <th>Accountant Name</th>
                                    <th>MIPL Location</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {listLoading && (
                                    <tr>
                                        <td colSpan={9} className="text-center py-4 text-gray-500">
                                            Loading…
                                        </td>
                                    </tr>
                                )}
                                {!listLoading && rows.map((driver, index) => (
                                    <tr key={driver.id || `row-${index}`}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{driver.driverId}</td>
                                        <td>{driver.name}</td>
                                        <td>{driver.mobile}</td>
                                        <td>{driver.licence}</td>
                                        <td>{driver.accountant}</td>
                                        <td>{driver.location}</td>
                                        <td>{driver.status}</td>

                                        <td>
                                            <div className="flex items-center justify-center gap-2">
                                                <PermissionGate module={MODULES.DRIVER} action="edit">
                                                    <label className="relative inline-flex cursor-pointer items-center w-9 min-w-9 h-5 rounded-full bg-[#E11818] my-0 [&:has(input:checked)]:bg-[#17B517] [&:has(input:checked)_.absolute]:!left-[17px] transition-all">
                                                        <input
                                                            type="checkbox"
                                                            className="hidden"
                                                            checked={driver.statusCode === 1}
                                                            onChange={() => handleDriverStatusToggle(driver.id, driver.statusCode)}
                                                            disabled={statusUpdatingId === driver.id || listLoading}
                                                        />
                                                        <div className="absolute left-[3px] top-0.5 h-4 w-4 rounded-full bg-white transition-all" />
                                                    </label>
                                                </PermissionGate>
                                                <PermissionGate module={MODULES.DRIVER} action="edit">
                                                    <Link to={`/driver-management/edit-driver-details?driver_id=${encodeURIComponent(driver.id)}${detailListQuery}`}>
                                                        <img src={EditIcon} alt="Edit" className='h-7 min-w-[22px]'/>
                                                    </Link>
                                                </PermissionGate>
                                                <PermissionGate module={MODULES.DRIVER} action="view">
                                                    <Link to={`/driver-management/view-driver-details?driver_id=${encodeURIComponent(driver.id)}${detailListQuery}`}>
                                                        <img src={EyeIcon} alt="View" className='h-7 min-w-[22px]'/>
                                                    </Link>
                                                </PermissionGate>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!listLoading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="text-center py-4 text-gray-500">
                                            No Driver Found!
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="flex gap-x-4 items-center mt-4">

                    <select
                        className='p-2 shadow-sm outline-none max-[767px]:text-xs'
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">
                        {listLoading
                            ? '—'
                            : totalRecords === 0
                              ? '0 records found'
                              : `${displayFrom}–${displayTo} of ${totalRecords} records found`}
                    </div>
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={listLoading || currentPage === 1}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                                currentPage === 1 ? '' : 'cursor-pointer'
                            }`}
                        >
                            <img src={LeftAngle} className='invert grayscale brightness-100' alt="Previous" />
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">
                            of {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={listLoading || currentPage === totalPages || totalRecords === 0}
                            className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center cursor-pointer"
                        >
                            <img src={RightAngle} className='invert grayscale brightness-100' alt="Next" />
                        </button>

                    </div>
                </div>

            </div>

            <MasterSuccessModal
                open={successModal.open}
                message={successModal.message}
                onClose={closeMasterSuccess}
            />
                        </>
    );
};

export default DriverManagement;
